<template>
  <div class="championship-overview-page" :style="rootStyle">
    <!-- Top bar: back to the list the user came from (championships in broker admin, competitions elsewhere) -->
    <div class="top-bar">
      <button type="button" class="back-link" @click="goBack">
        <i class="fa-regular fa-arrow-left" aria-hidden="true"></i>
        <span>{{ $t($isBrokerApp ? 'championship.backToChampionships' : 'championship.backToCompetitions') }}</span>
      </button>
      <span v-if="usingMockData" class="mock-tag">{{ $t('championship.mockData') }}</span>
    </div>

    <!-- Header row: title + status on the left, stage switcher on the right
         (SFB-1290 — qualifiers and play-offs live in the same details view) -->
    <header class="championship-header">
      <div class="header-main">
        <div class="header-title-row">
          <h1 class="header-title">{{ overview ? overview.name : $t('championship.loading') }}</h1>
          <span v-if="statusBadge" class="status-tag" :class="statusBadgeClass">{{ statusBadge }}</span>
        </div>
        <!-- "Organized by [sponsor]" subtitle hidden for now (remove v-if to restore). -->
        <p v-if="false" class="header-subtitle">
          {{ $t('championship.organizedBy', { name: organizerName || $t('championship.sponsorName') }) }}
        </p>
      </div>

      <div class="tabs-pill" role="tablist">
        <button
          v-if="hasQualifiers"
          type="button"
          class="tab"
          role="tab"
          :aria-selected="activeTab === 'QUALIFIERS'"
          :class="{ active: activeTab === 'QUALIFIERS' }"
          @click="activeTab = 'QUALIFIERS'"
        >
          <i class="fa-regular fa-list-ul" aria-hidden="true"></i>
          {{ $t('championship.tabs.qualifiers') }}
        </button>
        <button
          type="button"
          class="tab"
          role="tab"
          :aria-selected="activeTab === 'BRACKETS'"
          :class="{ active: activeTab === 'BRACKETS' }"
          @click="activeTab = 'BRACKETS'"
        >
          <i class="fa-regular fa-chart-simple" aria-hidden="true"></i>
          {{ $t('championship.tabs.playoffs') }}
        </button>
        <!-- Members tab hidden for now (SFB-1151). ChampionshipMembers still
             renders when activeTab === 'MEMBERS'. -->
        <button
          v-if="false"
          type="button"
          class="tab"
          :class="{ active: activeTab === 'MEMBERS' }"
          @click="activeTab = 'MEMBERS'"
        >
          <i class="fa-regular fa-users" aria-hidden="true"></i>
          {{ $t('championship.tabs.members') }}
        </button>
      </div>
    </header>

    <ChampionshipBracketSkeleton v-if="loading && activeTab === 'BRACKETS'" :column-count="4" />

    <div v-else-if="loading" class="loading-state">
      <div class="loading-spinner" aria-hidden="true"></div>
      <span class="loading-text">{{ $t('championship.loading') }}</span>
    </div>

    <ChampionshipBracket
      v-else-if="activeTab === 'BRACKETS'"
      :overview="overview"
      :bracket="bracket"
      :participants="participants"
      @match-click="onMatchClick"
      @stage-advanced="onStageAdvanced"
      @stage-advance-failed="onStageAdvanceFailed"
    />

    <ChampionshipQualifiers
      v-else-if="activeTab === 'QUALIFIERS'"
      :overview="overview"
      :participants="participants"
    />

    <ChampionshipMembers
      v-else
      :overview="overview"
      :participants="participants"
    />

    <ChampionshipMatchDetailsDrawer
      :match="selectedMatch"
      :overview="overview"
      :stages-by-id="stagesById"
      :participants="participants"
      @close="selectedMatch = null"
    />

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3500" top>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import ChampionshipBracket from '@/views/Championship/components/ChampionshipBracket.vue';
import ChampionshipBracketSkeleton from '@/views/Championship/components/ChampionshipBracketSkeleton.vue';
import ChampionshipMembers from '@/views/Championship/components/ChampionshipMembers.vue';
import ChampionshipQualifiers from '@/views/Championship/components/ChampionshipQualifiers.vue';
import ChampionshipMatchDetailsDrawer from '@/views/Championship/components/ChampionshipMatchDetailsDrawer.vue';
import {
  getChampionshipOverview,
  getChampionshipBracket,
  listChampionshipParticipants,
  isMockMode,
  resolveAccentColor,
} from '@/services/championship.service';

export default {
  name: 'ChampionshipOverview',
  components: { ChampionshipBracket, ChampionshipBracketSkeleton, ChampionshipMembers, ChampionshipQualifiers, ChampionshipMatchDetailsDrawer },
  data() {
    return {
      overview: null,
      bracket: null,
      participants: [],
      activeTab: 'BRACKETS',
      tabChosen: false,
      loading: true,
      usingMockData: false,
      selectedMatch: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    championshipId() {
      return this.$route.query.championshipId
        || this.$route.params.championshipId
        || null;
    },
    statusBadge() {
      switch (this.overview?.status) {
        case 'RUNNING': return this.$t('championship.status.live');
        case 'PLANNING': return this.$t('championship.status.planning');
        case 'FINISHED': return this.$t('championship.status.finished');
        case 'REVIEWING': return this.$t('championship.status.reviewing');
        case 'CANCELLED': return this.$t('championship.status.cancelled');
        case 'DRAFT': return this.$t('championship.status.draft');
        default: return null;
      }
    },
    statusBadgeClass() {
      switch (this.overview?.status) {
        case 'RUNNING': return 'status-running';
        case 'FINISHED': return 'status-finished';
        case 'CANCELLED': return 'status-cancelled';
        default: return 'status-neutral';
      }
    },
    organizerName() {
      return this.$store.state.tournament?.tournamentOrganizer || null;
    },
    stagesById() {
      const out = {};
      (this.overview?.stages || []).forEach((s) => { out[s.id] = s; });
      return out;
    },
    hasQualifiers() {
      return (this.overview?.stages || []).some((s) => s.stageType === 'QUALIFICATION');
    },
    accentColor() {
      // Reactive — pulls from broker store first, then IB, then trader-side
      // club color, finally the brand red default. CSS variable propagation
      // is what cascades the colour into bracket headers, tabs, drawer chips.
      return resolveAccentColor(this.$store);
    },
    rootStyle() {
      return { '--championship-accent': this.accentColor };
    },
  },
  watch: {
    championshipId: {
      immediate: true,
      handler() { this.load(); },
    },
    activeTab(val, old) {
      // A manual switch must survive the reloads triggered by advancing a stage.
      if (old !== undefined && !this.loading) this.tabChosen = true;
    },
    '$route.query.lang': {
      immediate: true,
      handler() { this.applyLangFromQuery(); },
    },
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      const fallback = this.$isBrokerApp ? '/championships' : (this.$isIBApp ? '/competitions' : '/club/tournaments');
      this.$router.push(fallback).catch(() => {});
    },
    // Public view: a `?lang=` query param renders the championship in that
    // language (e.g. ?lang=es). Ignored if the locale isn't loaded so we never
    // switch to an unsupported language.
    applyLangFromQuery() {
      const lang = this.$route.query.lang;
      if (lang && this.$i18n.availableLocales.includes(lang) && this.$i18n.locale !== lang) {
        this.$i18n.locale = lang;
      }
    },
    async load() {
      const id = this.championshipId;
      if (!id) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const [overview, bracket, participants] = await Promise.all([
          getChampionshipOverview(id),
          getChampionshipBracket(id),
          listChampionshipParticipants(id),
        ]);
        this.overview = overview;
        this.bracket = bracket;
        this.participants = participants;
        // Land on the qualifiers when the championship has that stage (the
        // first thing a participant lives through); otherwise on the bracket.
        if (!this.tabChosen) {
          this.activeTab = this.hasQualifiers ? 'QUALIFIERS' : 'BRACKETS';
        }
        // Heuristic: if mock IDs come back (we know the mock uses 9999), or
        // explicit mock-mode is on, surface it in the header so QA knows.
        this.usingMockData = isMockMode() || (overview && overview.id === 9999);
      } catch (err) {
        console.error('[championship] load failed', err);
      } finally {
        this.loading = false;
      }
    },
    onMatchClick(match) {
      this.selectedMatch = match;
    },
    onStageAdvanced(stage) {
      this.snackbarText = this.$t('championship.bracket.advancedSuccess', {
        stage: this.$t(`championship.stage.${stage.stageType}`),
      });
      this.snackbarColor = 'success';
      this.snackbar = true;
      // Re-pull data so the next stage's slots reflect the new winners.
      this.load();
    },
    onStageAdvanceFailed({ error }) {
      this.snackbarText = error?.response?.data?.message
        || this.$t('championship.bracket.advancedError');
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.championship-overview-page {
  font-family: 'Avenir', sans-serif;
  padding: 24px 32px 80px;
  width: 100%;
  max-width: 1440px;
  margin: 0 0;
  position: relative;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  letter-spacing: 0.28px;
  color: #ababab;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover { color: #14161b; }
}

.championship-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.header-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: 16px; // breathing room between the championship name and the status pill (Figma)
  flex-wrap: wrap;
}

.header-title {
  font-family: 'Aeonik', 'Avenir', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1b1b1b;
  margin: 0;
}

.status-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-left: 4px; // belt-and-suspenders spacing on top of the gap above

  &.status-running { background: #d6f5e3; color: #009f63; }
  &.status-finished { background: #f1f1f1; color: #6a6a6a; }
  &.status-cancelled { background: #fdecea; color: #c42a14; }
  &.status-neutral { background: #f1f1f1; color: #6a6a6a; }
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #6a6a6a;
}

// Stage switcher (Figma: grey pill track, active segment in the accent colour)
.tabs-pill {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  background: #ebebeb;
  border-radius: 10px;
  padding: 4px;
  gap: 5px;
  height: 40px;

  .tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 100%;
    background: transparent;
    border: none;
    padding: 0 12px;
    font-size: 15px;
    font-weight: 500;
    color: #14161b;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;

    i { font-size: 14px; }

    &:hover:not(.active) { background: rgba(255, 255, 255, 0.6); }

    &.active {
      background: var(--championship-accent, #c03131);
      color: #fff;
    }
  }
}

.mock-tag {
  background: #fff5d1;
  color: #8a6d00;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 10px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: #9a9a9a;
  padding: 60px 0;
  font-size: 14px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid rgba(0, 0, 0, 0.08);
  border-top-color: var(--championship-accent, #c03131);
  animation: championship-spin 0.85s linear infinite;
}

.loading-text { color: #6a6a6a; }

@keyframes championship-spin {
  to { transform: rotate(360deg); }
}

.theme--dark .championship-overview-page {
  .header-title { color: #f8f8f8; }
  .back-link:hover { color: #f8f8f8; }
  .tabs-pill { background: rgba(255, 255, 255, 0.06); .tab { color: #f8f8f8; } }
}

@media (max-width: 600px) {
  .championship-overview-page { padding: 16px 16px 60px; }
  .tabs-pill { width: 100%; .tab { flex: 1; justify-content: center; } }
}
</style>
